import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from "@angular/router";
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { BiddingService } from 'src/app/services/bidding.service';
import { environment } from '../../../environments/environment';
import { ProfileSetting } from '../../pages/profile/profile.component';
import { EnrollmentsService } from '../../services/enrollments.service';
import { TileModalComponent } from '../modals/tile-modal/tile-modal.component';
import { AuthenticationService, LoginState } from '../services/authentication.service';
import { CmsService } from '../services/cms.service';
import { LogCategory, LogCoreComponent, LoggerService, LogType } from '../services/logger.service';
import { ProfileService } from '../services/profile.service';
import { SidebarService } from '../services/sidebar.service';
import { TagManagerService } from '../services/tag-manager.service';
import { UtilityService } from '../services/utility.service';


@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
    //  MyKE Version number currently defaulting to 1.  Do we want to control this with a setting instead?
    @Input() version: number = 1;

    public asideIsOpen = false;
    public routeUrl: Router;
    public currentDate: Date;
    public pageStore$: BehaviorSubject<any>;
    public notify: boolean
    public alertClosed: boolean;
    public registration: boolean;
    public bidding: boolean;
    private _registrationSubscriber: Subscription;
    private _destroy$: Subject<boolean> = new Subject<boolean>();
    public dayMessage: string = '';
    public searchFilters: string = "";


    public get prod(): boolean {
        return environment.production; 
    }

    constructor(
        public cmsService: CmsService,
        public sidebarService: SidebarService,
        private logger: LoggerService,
        private gtm: TagManagerService,
        public auth: AuthenticationService,
        public profile: ProfileService,
        public utility: UtilityService,
        public enrl: EnrollmentsService,
        public bidservice: BiddingService,
        private modalService: NgbModal,
        private _router: Router

    ) {
        this.log("constructor", null, LogCategory.Constructor);
        this.getCurrentMessage();
        this.routeUrl = _router;
        this.currentDate = new Date();
        this.pageStore$ = this.profile.profile$.pipe(
            filter((profile) => !!profile),
            map((profile) => this.profile.pageStore)
        ) as BehaviorSubject<any>;



        this._registrationSubscriber = enrl.hasActiveDropAdd$.pipe(takeUntil(this._destroy$)).subscribe(
            (rounds) => {
                if (rounds.some(i => i.active == true)) {
                    this.registration = true;
                } else {
                    this.registration = false;
                }
            }
        );

        // TODO
        // * We shouldn't need a registration subscriber or _destroy -- we can just stack an observable on top of
        //     hasActiveDropAdd$ that maps the emitted value of that observable to a true or false so that we can use
        //     async pipe in the html and not have a subscribe leak.
        // * we should go back and safely type hasActiveDropAdd$ to at least an any[] (instead of just any), and ideally
        //     to something more descriptive if that's possible without creating a new class
        // * same for bidding below
        // * the HTML becomes simething like : <span *ngIf="isInBidding$ | async" class="register-now">
        // * and isInBidding can be defined like this:     public isInBidding$: Observable<boolean>; 
        // * something like this: 
        //this.isInBidding$ = enrl.hasActiveDropAdd$.pipe(
        //    map((rounds) => rounds.some(i => i.active == true) )
        //);


        bidservice.activeAuctions$.pipe(takeUntil(this._destroy$)).subscribe(
            (auctions) => {               
                if (auctions.some(i => i.active == true)) {                   
                    this.bidding = true;
                } else {
                    this.bidding = false;
                }                
            }
        );

    }

    public GetExternalLinks$() : Observable<any[]> {

        this.cmsService.currentContentType$.next('externallink');
        return this.cmsService.getContentByType$;
    }

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.CoreComponent, LogCoreComponent.Header, category);
    }

    ngOnInit(): void {        
        this.log("ngOnInit", null, LogCategory.Init);
    }

    ngOnDestroy() {
        this._destroy$.next(true);
        this._destroy$.complete();

        this.log("ngOnDestroy", null, LogCategory.Destroy);
    }

    public toggleAside() {
        this.asideIsOpen = !this.asideIsOpen;
        this.log("toggleAside", "Toggle aside: " + this.asideIsOpen);
    }

    public closeAside() {
        this.asideIsOpen = false;
    }

    public childToggle(agree: boolean) {
        this.asideIsOpen = agree;
    }

    public toggleShowDebug(): void {
        environment.showDebug = !environment.showDebug;
    }

    public getStateMessage (state : LoginState ): string {
        switch (state) {
            case LoginState.loggedout:
                return "logged out";
            case LoginState.authenticating:
                return "authenticating...";
            case LoginState.token_exchange:
                return "verifying credentials...";
            case LoginState.loggedin:
            case LoginState.refreshable:
            case LoginState.spoofing:
            case LoginState.spoofing_refreshable:

                return "logged in";
            case LoginState.expired:
                return "session expired";
            case LoginState.unknown:
                return "logged out";
        }
    }

    public getCurrentMessage() {
        let date: Date = new Date();
        let hours: number = date.getHours();

        if (hours < 12)
            this.dayMessage = "Good morning";
        else if (hours >= 12 && hours < 17)
            this.dayMessage = "Good afternoon";
        else
            this.dayMessage = "Good evening";
    }

    public showLoginButton(state: LoginState): boolean {
        switch (state) {
            case LoginState.loggedout:
            case LoginState.expired:
            case LoginState.unknown:
                return true;
            case LoginState.authenticating:
            case LoginState.token_exchange:
            case LoginState.loggedin:
            case LoginState.refreshable:
            case LoginState.spoofing:
            case LoginState.spoofing_refreshable:
                return false; 
        }
    }

    public search() {
        //ga - log search click
        this.gtm.gaSearch('Site');
        //Using settimeout because there is a delay in the routerlink being built with parameters in html
        //If there is no delay pressing enter after typing in search input may leave off characters in the query
        setTimeout(() => { document.getElementById("searchLink").click(); }, 500)
    }

    public openModal(showLeft: boolean, title: string, widgetName: string, modalSize: string) {
        var modalRef;
        if (modalSize == "")
            modalSize = "modal-lg"

        if (showLeft)
            modalRef = this.modalService.open(TileModalComponent, { windowClass: 'left', keyboard:false });
        else
            modalRef = this.modalService.open(TileModalComponent, {
                modalDialogClass: modalSize, keyboard: false
            });

        modalRef.componentInstance.title = title;
        modalRef.componentInstance.componentName = widgetName;
    }

    public selectVersion() {
        var data: ProfileSetting[] = [new ProfileSetting()]

        var profile = this.profile.profile;
        profile.version = profile.version == 1 ? 2 : 1;

        data[0].Id = ".Myke.Version";
        data[0].Type = "byte";
        data[0].Value = profile.version.toString();


        var redirect = false;

        // we need to check if the user is audienced for this page before switching.       
        var pagename = this._router.url.replace("/", "");
        var page = pagename.charAt(0).toUpperCase() + pagename.slice(1); //convert to titlecase        
        //ignore dashboard       
        if (page != 'Dashboard' && page != 'Profile') {
            if (profile.version < 2)
                redirect = !this.profile.pageStore[page];
            else
                redirect = !this.profile.componentStore[page];
        }       

        switch (this._router.url) {
            case '/':
            case '/catalog':
            case '/dashboard':
            case '/people':
            case '/profile':
                break;
            default:
                redirect = true;
        }

        this.profile.updateSettings(data, true, redirect);
    }
}
